import React, { JSX, useEffect, useRef, use, useState, useMemo } from 'react';
import {
  Text,
  RichText,
  Field,
  LinkField,
  Link,
  ImageField,
  Image,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ItemValue } from 'components/types/ListItem';
import ThemeContext from 'components/context/ThemeContext';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';
import { observeInView } from 'src/hooks/observeInView';

type SpotlightTallProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    DesktopBackgroundImage: ImageField;
    MobileBackgroundImage?: ImageField;
    BaseHeadline: Field<string>;
    BaseDescription: Field<string>;
    BaseAlignment: ItemValue;
    BaseOverlayColor: ItemValue;
    BaseLink: LinkField;
    BaseLinkType: ItemValue;
    Parallax: {
      value: boolean;
    };
    PhotoVariant: ItemValue;
    TopSurfaceAccent: ImageField;
    BottomSurfaceAccent: ImageField;
    AccentAlignment: ItemValue;
    InlineImage: ImageField;
    DarkText: {
      value: boolean;
    };
  };
};

const SpotlightTall = (props: SpotlightTallProps): JSX.Element => {
  if (!props || !props.fields) {
    return <div></div>;
  }
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );
  const themeContext = use(ThemeContext);
  const theme = themeContext?.themeState?.theme;
  const elementRef = useRef<HTMLDivElement | null>(null);
  const animRef = useRef<HTMLDivElement | null>(null);
  let prevScroll = 0;
  const [isLgUp, setIsLgUp] = useState(false);
  const { fields } = props;

  const handleScroll = () => {
    if (
      elementRef.current &&
      (theme === 'sfcon' || theme === 'sacon') &&
      elementRef.current.classList.contains('parallax')
    ) {
      const rect = elementRef.current?.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        if (rect.top < prevScroll) {
          const currImg = elementRef.current.querySelector('img');
          if (currImg) {
            const imgTransform =
              (-(currImg.height - rect.height) / 2) *
              ((window.innerHeight - (rect.top - rect.height / 2)) /
                window.innerHeight);
            currImg.style.transform = `translateY(${imgTransform}px)`;
          }
        } else {
          const currImg = elementRef.current.querySelector('img');
          if (currImg) {
            const imgTransform =
              (-(currImg.height - rect.height) / 2) *
              ((window.innerHeight - (rect.top - rect.height / 2)) /
                window.innerHeight);
            currImg.style.transform = `translateY(${imgTransform}px)`;
          }
        }
      }
      prevScroll = rect.top;
    }
  };

  useEffect(() => {
    if (!animRef.current) return;

    observeInView(animRef.current);
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      observeInView(elementRef.current);
      const rect = elementRef.current?.getBoundingClientRect();
      prevScroll = rect.top;
    }
  }, [elementRef]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (theme === 'sfcon' || theme === 'sacon') &&
      elementRef?.current?.classList.contains('parallax')
    ) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => window.removeEventListener('resize', handleScroll);
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsLgUp('matches' in e ? e.matches : (e as MediaQueryList).matches);
    onChange(mq);
    mq.addEventListener?.(
      'change',
      onChange as (e: MediaQueryListEvent) => void
    );
    return () => {
      mq.removeEventListener?.(
        'change',
        onChange as (e: MediaQueryListEvent) => void
      );
    };
  }, []);

  const chosenImage: ImageField | undefined = useMemo(() => {
    const desktop = fields.DesktopBackgroundImage?.value?.src
      ? fields.DesktopBackgroundImage
      : undefined;
    const mobile = fields.MobileBackgroundImage?.value?.src
      ? fields.MobileBackgroundImage
      : undefined;
    if (desktop && mobile) return isLgUp ? desktop : mobile;
    return desktop ?? mobile;
  }, [fields.DesktopBackgroundImage, fields.MobileBackgroundImage, isLgUp]);
  const hasImage = Boolean(chosenImage?.value?.src);
  const safeAlt = chosenImage?.value?.alt ?? fields?.BaseHeadline?.value ?? '';

  return (
    <div
      className={classNames('overhauled', marginSize)}
      id={`spotlight-tall-${props.rendering.uid}`}
      key={`spotlight-tall-${props.rendering.uid}`}
    >
      <div
        className={classNames(
          'spotlight tall',
          (theme === 'sfcon' || theme === 'sacon') &&
            fields?.Parallax?.value === true
            ? 'parallax'
            : '',
          fields?.PhotoVariant?.name && fields.PhotoVariant.name.toLowerCase()
        )}
        ref={elementRef}
        id={`spotlight-tall-${props.rendering.uid}`}
        key={`spotlight-tall-${props.rendering.uid}`}
      >
        {hasImage && (
          <Image
            field={chosenImage as ImageField}
            alt={safeAlt}
            className="bg-img"
          />
        )}
        {fields.TopSurfaceAccent?.value && (
          <Image
            field={fields.TopSurfaceAccent}
            alt={safeAlt}
            className={classNames(
              'top-accent',
              fields.AccentAlignment?.name?.toLowerCase()
            )}
          />
        )}
        {fields.BottomSurfaceAccent?.value && (
          <Image
            field={fields.BottomSurfaceAccent}
            alt={safeAlt}
            className={classNames(
              'bottom-accent',
              fields.AccentAlignment?.name?.toLowerCase()
            )}
          />
        )}
        {!props.fields.PhotoVariant?.name && (
          <div
            className={classNames(
              fields.BaseOverlayColor?.name !== 'none'
                ? 'overlay spotlight-tall-gradient'
                : '',
              fields?.BaseAlignment?.name
                ? fields?.BaseAlignment?.name?.toLowerCase()
                : '',
              fields.BaseOverlayColor?.name !== 'none' &&
                fields?.BaseOverlayColor?.name
                ? fields?.BaseOverlayColor?.name?.toLowerCase()
                : ''
            )}
          ></div>
        )}
        <div
          className={classNames(
            'content-wrapper jss-inner-container',
            fields?.BaseAlignment?.name &&
              `align-${fields?.BaseAlignment?.name?.toLowerCase()}`,
            'spotlight-talls-anim animated'
          )}
          ref={animRef}
          key={props.rendering?.uid}
          id={props.rendering?.uid}
        >
          <div
            className={classNames(
              'text-content vertical-spacing',
              fields?.DarkText?.value === true ? 'dark-text' : '',
              fields?.PhotoVariant?.name &&
                `jss-inner-container ${fields.PhotoVariant.name.toLowerCase()}`
            )}
          >
            {fields?.BaseHeadline?.value && (
              <Text tag="h2" className="headline" field={fields.BaseHeadline} />
            )}
            {fields?.BaseDescription?.value && (
              <RichText
                tag="p"
                className={
                  !(props.fields.BaseHeadline.value.length > 0) &&
                  !(
                    props.fields.BaseLink?.value?.href &&
                    props.fields.BaseLink?.value?.href?.length > 0
                  )
                    ? 'osf-display-h2'
                    : 'description'
                }
                field={props.fields.BaseDescription}
              />
            )}
            {fields?.BaseLink?.value?.href && fields?.BaseLinkType?.name && (
              <Link
                field={fields?.BaseLink?.value}
                className={classNames(
                  fields?.BaseLinkType
                    ? `btn-${fields?.BaseLinkType?.name.toLowerCase()}`
                    : '',
                  theme === 'osfhealthcare' || props?.fields?.PhotoVariant?.name
                    ? 'btn-mobile-fw'
                    : ''
                )}
              />
            )}
          </div>
          {fields?.PhotoVariant?.name && (
            <Image
              field={fields?.InlineImage}
              alt={safeAlt}
              className="inline-img"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SpotlightTall;
